/**
 * OAuth Authentication Callback Handler
 * This page handles the OAuth callback and sends the token to the parent window
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authApi';
import {
  OAUTH_GENERIC_MESSAGE as OAUTH_FALLBACK_MESSAGE,
  oauthErrorMessage,
  publicSafeErrorMessage,
} from '../utils/apiErrorDetail';

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string>('');
  const hasHandledRef = useRef(false);

  useEffect(() => {
    if (hasHandledRef.current) {
      return;
    }
    hasHandledRef.current = true;
    const handleCallback = async () => {
      console.log('[AuthCallback] ===== CALLBACK PAGE LOADED =====');
      console.log('[AuthCallback] Full URL:', window.location.href);

      let isPopup = false;
      let openerRef: Window | null = null;
      let channelId: string | null = null;
      let storageKey: string | null = null;
      let windowNameSuggestsPopup = false;

      const emitResult = (message: { type: 'oauth-success' | 'oauth-error'; token?: string; user?: any; error?: string }) => {
        if (channelId) {
          const fallbackKey = storageKey;
          if ('BroadcastChannel' in window) {
            try {
              console.log('[AuthCallback] Broadcasting message via channel:', channelId, message.type);
              const channel = new BroadcastChannel(`oauth-popup-${channelId}`);
              channel.postMessage(message);
              channel.close();
            } catch (error) {
              console.warn('[AuthCallback] Failed to send BroadcastChannel message:', error);
            }
          }
          if (fallbackKey) {
            try {
              console.log('[AuthCallback] Writing storage fallback result:', message.type);
              localStorage.setItem(
                fallbackKey,
                JSON.stringify({
                  ...message,
                  timestamp: Date.now(),
                })
              );
            } catch (error) {
              console.warn('[AuthCallback] Failed to write storage fallback message:', error);
            }
          }
          if (fallbackKey) {
            setTimeout(() => {
              try {
                localStorage.removeItem(fallbackKey);
              } catch (cleanupError) {
                console.warn('[AuthCallback] Failed to cleanup storage fallback key:', cleanupError);
              }
            }, 5000);
          }
        }

        if (isPopup && openerRef) {
          try {
            openerRef.postMessage(message, window.location.origin);
          } catch (error) {
            console.warn('[AuthCallback] Failed to postMessage to opener:', error);
          }
        }
      };

      try {
        // Check if this is being opened in a popup
        isPopup = !!(window.opener && window.opener !== window);
        openerRef = isPopup ? window.opener : null;
        console.log('[AuthCallback] Is popup?', isPopup);
        console.log('[AuthCallback] window.opener exists?', !!window.opener);
        console.log('[AuthCallback] window.opener type:', typeof window.opener);
        console.log('[AuthCallback] window.name:', window.name);
        windowNameSuggestsPopup = Boolean(
          window.name && (window.name.includes('Login') || window.name.includes('Sign up'))
        );

        // Get the full URL with the token from the backend
        const urlParams = new URLSearchParams(window.location.search);
        const hash = window.location.hash;
        channelId =
          urlParams.get('channel_id') ||
          (hash ? new URLSearchParams(hash.substring(1)).get('channel_id') : null);
        storageKey = channelId ? `oauth_result_${channelId}` : null;
        console.log('[AuthCallback] Channel ID:', channelId);

        console.log('[AuthCallback] URL search params:', window.location.search);
        console.log('[AuthCallback] URL hash:', hash);

        // Check if we have an access token in the URL
        // FastAPI-users may return it in different ways
        const popupParam =
          urlParams.get('popup') ||
          (hash ? new URLSearchParams(hash.substring(1)).get('popup') : null);
        const popupParamSuggestsPopup = popupParam === '1' || popupParam === 'true';

        const errorParam =
          urlParams.get('error') ||
          (hash ? new URLSearchParams(hash.substring(1)).get('error') : null);
        let token = urlParams.get('access_token');

        if (!token) {
          const hrefMatch = window.location.href.match(/[?#&]access_token=([^&#]+)/);
          if (hrefMatch && hrefMatch[1]) {
            token = decodeURIComponent(hrefMatch[1]);
          }
        }

        // Check hash for token (common in OAuth flows)
        if (!token && hash) {
          const hashParams = new URLSearchParams(hash.substring(1));
          token = hashParams.get('access_token');
        }

        if (token) {
          console.log('[AuthCallback] Token received:', token.substring(0, 20) + '...');

          // Remove sensitive query parameters from the URL bar
          window.history.replaceState(null, '', window.location.pathname);

          // Store the token
          console.log('[AuthCallback] Setting token in localStorage...');
          authService.setToken(token);

          // Verify token was stored
          const storedToken = authService.getToken();
          console.log('[AuthCallback] Token stored?', !!storedToken);
          console.log('[AuthCallback] Stored token matches?', storedToken === token);
          console.log('[AuthCallback] Stored token preview:', storedToken ? storedToken.substring(0, 20) + '...' : 'null');

          const shouldTreatAsPopup = isPopup || windowNameSuggestsPopup || popupParamSuggestsPopup;
          const shouldNotifyParent = shouldTreatAsPopup || !!channelId;

          const successPayload = {
            type: 'oauth-success' as const,
            token,
            user: null,
          };

          if (shouldNotifyParent) {
            console.log('[AuthCallback] Notifying opener/listeners about OAuth success...');
            emitResult(successPayload);
          }

          if (shouldTreatAsPopup) {
            console.log('[AuthCallback] Popup context detected, closing window after notifying parent');
            setStatus('success');
            setTimeout(() => {
              console.log('[AuthCallback] Closing popup...');
              window.close();
            }, 500);
          } else {
            // For non-popup mode: Fetch user and redirect to upload page
            console.log('[AuthCallback] Not a popup, fetching user and redirecting to upload page...');
            try {
              const user = await authService.getCurrentUser();
              console.log('[AuthCallback] User fetched:', user);
              setStatus('success');
              setTimeout(() => {
                navigate('/upload');
              }, 1000);
            } catch (userError) {
              console.error('[AuthCallback] Failed to fetch user, but token is stored. Redirecting anyway...', userError);
              setStatus('success');
              setTimeout(() => {
                navigate('/upload');
              }, 1000);
            }
          }
        } else {
          // `errorParam` comes out of the callback URL, so it is whatever the
          // caller put there. Recognized OAuth codes become fixed copy; the
          // rest never reach the screen or the opener.
          if (errorParam) {
            console.error('[AuthCallback] provider error param:', errorParam);
          }
          throw new Error(
            oauthErrorMessage(errorParam, OAUTH_FALLBACK_MESSAGE),
          );
        }
      } catch (err: any) {
        console.error('OAuth callback error:', err);
        // Allow-list, not deny-list: this screen can be reached with a crafted
        // URL, and whatever it shows it also broadcasts to the opener.
        const shown = publicSafeErrorMessage(err, OAUTH_FALLBACK_MESSAGE);
        setError(shown);
        setStatus('error');

        emitResult({
          type: 'oauth-error',
          error: shown,
        });

        if (isPopup || windowNameSuggestsPopup) {
          setTimeout(() => {
            console.log('[AuthCallback] Attempting to close popup after error...');
            window.close();
          }, 3000); // Increased delay to 3 seconds for error cases
        } else {
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Completing authentication...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we log you in.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Authentication successful!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {window.opener ? 'You can close this window.' : 'Redirecting to upload page...'}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Authentication failed
            </h2>
            <p className="mt-2 text-sm text-red-600">{error}</p>
            <p className="mt-2 text-sm text-gray-600">
              {window.opener ? 'This window will close automatically.' : 'Redirecting to login...'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
